import {
  ART_PREFERENCES_MAX,
  artPreferencesSchema,
  downloadName,
  typographic,
  type QUOTE_EXPORT_SIZES,
  type QuoteCard,
  type QuoteCardLayout,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useServiceAccess } from '../access/useAccess.ts'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import { servicesKeys, usePlan, useSeries } from './useServices.ts'
import { aiKeys, useAiCost } from './useSuggestions.ts'

/**
 * AI creative (docs/17-AI-CREATIVE.md): a series' art — sets of three
 * directions, each a main graphic and, when asked for, its matching slide
 * background — and a plan's quote cards. The screens' logic; the markup is
 * in apps/web/src/components/services/ai-creative/.
 *
 * Nothing is applied by itself: a person chooses "Use as series art" or
 * "Use as slide background", and exports the cards they want. While the
 * worker draws, the lists ask again every few seconds.
 */
export const creativeKeys = {
  seriesArt: (seriesId: string) => [...servicesKeys.series(seriesId), 'art-directions'] as const,
  quoteCards: (planId: string) => [...servicesKeys.plan(planId), 'quote-cards'] as const,
}

const POLL_MS = 3000

/** A direction's main graphic or slide background (the image id is its `?v=`). */
export const directionImageUrl = (
  seriesId: string,
  directionId: string,
  which: 'main' | 'background',
  imageId: string,
  download = false,
) =>
  `/api/v1/services/series/${seriesId}/art-directions/${directionId}/${which}?v=${encodeURIComponent(imageId)}${download ? '&download=true' : ''}`

/** The series' slide background (anyone who sees the series). */
export const slideBackgroundUrl = (seriesId: string, version: string) =>
  `/api/v1/services/series/${seriesId}/slide-background?v=${encodeURIComponent(version)}`

/** A quote-card set's background. */
export const quoteBackgroundUrl = (planId: string, setId: string, imageId: string) =>
  `/api/v1/services/plans/${planId}/quote-cards/${setId}/background?v=${encodeURIComponent(imageId)}`

// ---- series art ---------------------------------------------------------------------------------

/**
 * A series' art: its sets, newest first, a page at a time; "Make series
 * art" (or "3 more directions") with the person's preferences; per
 * direction "Make slide background", "Use as series art", "Use as slide
 * background". Its Editors' (`canEditSeries`); hidden without AI.
 */
export function useSeriesArt(seriesId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const access = useServiceAccess()
  const series = useSeries(seriesId)
  const editable =
    series.data !== undefined &&
    access.canEditSeries(series.data.plans.map((plan) => plan.serviceTypeId))

  const query = useInfiniteQuery({
    queryKey: creativeKeys.seriesArt(seriesId),
    enabled: editable && seriesId !== '',
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services
        .series({ id: seriesId })
        ['art-directions'].get({ query: pageParam ? { cursor: pageParam } : {} })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: (q) =>
      q.state.data?.pages.some((page) =>
        page.runs.some(
          (run) =>
            run.state === 'processing' ||
            run.directions.some(
              (d) => d.main.state === 'processing' || d.background.state === 'processing',
            ),
        ),
      )
        ? POLL_MS
        : false,
  })

  const first = query.data?.pages[0]
  const runs = query.data?.pages.flatMap((page) => page.runs) ?? []
  const more = first?.more ?? false
  const makeCost = useAiCost([more ? 'series_art_more' : 'series_art'])
  const backgroundCost = useAiCost(['series_art_background'])
  const [preferences, setPreferences] = useState('')
  const [preferencesError, setPreferencesError] = useState<string | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: creativeKeys.seriesArt(seriesId) })
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
  }
  /** The series itself changed (its art, its slide background): everything that shows it. */
  const refreshSeries = () => {
    refresh()
    void queryClient.invalidateQueries({ queryKey: servicesKeys.seriesList() })
    void queryClient.invalidateQueries({ queryKey: servicesKeys.series(seriesId) })
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plan'] })
  }

  const make = useMutation({
    mutationFn: async () => {
      const parsed = artPreferencesSchema.safeParse({ preferences })
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? 'Too long')
      const { data, error } = await api.api.v1.services
        .series({ id: seriesId })
        ['art-directions'].post({ preferences: parsed.data.preferences })
      if (error) throw error
      return data
    },
    onMutate: () => setPreferencesError(null),
    onSuccess: () => {
      setPreferences('')
      refresh()
    },
    onError: (error) => {
      if (error instanceof Error && !('status' in error)) setPreferencesError(error.message)
      else notifyFailure(error, undefined, 'Series art')
    },
  })

  const background = useMutation({
    mutationFn: async (directionId: string) => {
      const { error } = await api.api.v1.services
        .series({ id: seriesId })
        ['art-directions']({ directionId })
        .background.post()
      if (error) throw error
    },
    onSuccess: refresh,
    onError: (error) => notifyFailure(error, undefined, 'Slide background'),
  })

  const chooseArt = useMutation({
    mutationFn: async (directionId: string) => {
      const { data, error } = await api.api.v1.services
        .series({ id: seriesId })
        ['art-directions']({ directionId })
        .use.post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      refreshSeries()
      notify({
        tone: 'success',
        title: 'Series art changed',
        description: data.slideBackground
          ? 'Its slide background is the series’ too.'
          : 'Make its slide background to use it on slides.',
      })
    },
    onError: (error) => notifyFailure(error, undefined, 'Series art'),
  })

  const chooseBackground = useMutation({
    mutationFn: async (directionId: string) => {
      const { error } = await api.api.v1.services
        .series({ id: seriesId })
        ['art-directions']({ directionId })
        ['use-background'].post()
      if (error) throw error
    },
    onSuccess: () => {
      refreshSeries()
      notify({ tone: 'success', title: 'Slide background chosen' })
    },
    onError: (error) => notifyFailure(error, undefined, 'Slide background'),
  })

  const busy = runs.some((run) => run.state === 'processing')
  const inUse = first?.inUse ?? { artSourceImageId: null, slideBackground: null }

  return {
    query,
    /** AI is set up here and the viewer edits the series: the section shows. */
    shown: editable && first?.enabled === true,
    seriesName: series.data?.name ?? '',
    runs,
    /** A set is being made: one at a time. */
    busy,
    hasMore: query.hasNextPage,
    loadMore: () => void query.fetchNextPage(),
    loadingMore: query.isFetchingNextPage,
    /** Something was made before: the next set is "3 more directions". */
    more,
    makeLabel: more ? '3 more directions' : 'Make series art',
    makeCost,
    backgroundCost,
    preferences,
    setPreferences: (value: string) => {
      setPreferences(value)
      setPreferencesError(null)
    },
    preferencesMax: ART_PREFERENCES_MAX,
    preferencesError,
    make,
    background,
    chooseArt,
    chooseBackground,
    /** This direction's main graphic is the series' art. */
    isArt: (imageId: string | null) => imageId !== null && imageId === inUse.artSourceImageId,
    /** This background is the series' slide background. */
    isSlideBackground: (imageId: string | null) =>
      imageId !== null && imageId === inUse.slideBackground,
    imageUrl: (directionId: string, which: 'main' | 'background', imageId: string) =>
      directionImageUrl(seriesId, directionId, which, imageId),
    downloadUrl: (directionId: string, which: 'main' | 'background', imageId: string) =>
      directionImageUrl(seriesId, directionId, which, imageId, true),
  }
}

export type SeriesArtState = ReturnType<typeof useSeriesArt>
export type ArtRun = SeriesArtState['runs'][number]
export type ArtDirection = ArtRun['directions'][number]

/** What a run in progress is doing, in a few words. */
export function artRunPhase(run: ArtRun): string {
  if (run.state !== 'processing') return ''
  if (run.directions.length === 0) return 'Reading the series and choosing three directions…'
  const left = run.directions.filter((d) => d.main.state === 'processing').length
  return left > 0
    ? `Drawing ${left === 1 ? 'the last graphic' : `${left} graphics`} · about a minute`
    : 'Finishing…'
}

// ---- quote cards --------------------------------------------------------------------------------

/**
 * A plan's quote cards: its sets, newest first; "Make quote cards" on a
 * new background or the series' slide background; a new background for a
 * set; the words changed before export. Editors on the plan's service type,
 * once its sermon has been read.
 */
export function useQuoteCards(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const access = useServiceAccess()
  const plan = usePlan(planId)
  const editable = plan.data !== undefined && access.can(plan.data.serviceTypeId, 'editor')

  const query = useInfiniteQuery({
    queryKey: creativeKeys.quoteCards(planId),
    enabled: editable && planId !== '',
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        ['quote-cards'].get({ query: pageParam ? { cursor: pageParam } : {} })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: (q) =>
      q.state.data?.pages.some((page) =>
        page.sets.some(
          (set) => set.state === 'processing' || set.background.state === 'processing',
        ),
      )
        ? POLL_MS
        : false,
  })

  const first = query.data?.pages[0]
  const sets = query.data?.pages.flatMap((page) => page.sets) ?? []
  const makeCost = useAiCost(['quote_cards'])
  const seriesCost = useAiCost(['quote_cards_series_background'])
  const backgroundCost = useAiCost(['quote_background'])
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: creativeKeys.quoteCards(planId) })
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
  }

  const make = useMutation({
    mutationFn: async (background: 'new' | 'series') => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        ['quote-cards'].post({ background })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
    onError: (error) => notifyFailure(error, undefined, 'Quote cards'),
  })

  const newBackground = useMutation({
    mutationFn: async ({ setId, source }: { setId: string; source: 'new' | 'series' }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        ['quote-cards']({ setId })
        .background.post({ source })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
    onError: (error) => notifyFailure(error, undefined, 'Background'),
  })

  const save = useMutation({
    mutationFn: async ({ setId, cards }: { setId: string; cards: QuoteCard[] }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        ['quote-cards']({ setId })
        .patch({ cards })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
    onError: (error) => notifyFailure(error, undefined, 'Quote cards'),
  })

  return {
    query,
    /** AI is set up here and the viewer is an Editor of the plan: the section shows. */
    shown: editable && first?.enabled === true,
    /** Cards are made from the sermon once it has been read. */
    sermonRead: first?.sermonRead ?? false,
    /** The plan's series has a slide background the cards can use. */
    seriesBackground: first?.seriesBackground ?? false,
    sets,
    busy: sets.some((set) => set.state === 'processing'),
    hasMore: query.hasNextPage,
    loadMore: () => void query.fetchNextPage(),
    makeCost,
    seriesCost,
    backgroundCost,
    make,
    newBackground,
    save,
    planTitle: plan.data?.title ?? plan.data?.serviceTypeName ?? 'Quote',
    backgroundUrl: (setId: string, imageId: string) => quoteBackgroundUrl(planId, setId, imageId),
  }
}

export type QuoteCardsState = ReturnType<typeof useQuoteCards>
export type QuoteSet = QuoteCardsState['sets'][number]

/**
 * One card's words being changed before export: a draft of the set's cards,
 * saved together. Changing a verse's words makes them no longer the BSB's:
 * the translation goes from the card.
 */
export function useQuoteCardDraft(set: QuoteSet, onSave: (cards: QuoteCard[]) => void) {
  const [draft, setDraft] = useState<QuoteCard[]>(set.cards)
  const [base, setBase] = useState(set.cards)
  // The set changed under us (saved, or another tab): start from it.
  if (base !== set.cards) {
    setBase(set.cards)
    setDraft(set.cards)
  }
  const dirty = JSON.stringify(draft) !== JSON.stringify(set.cards)
  return {
    cards: draft,
    dirty,
    setText: (index: number, text: string) =>
      setDraft((cards) =>
        cards.map((card, i) =>
          i === index
            ? {
                ...card,
                text,
                translation: card.kind === 'verse' && text !== card.text ? null : card.translation,
              }
            : card,
        ),
      ),
    setReference: (index: number, reference: string) =>
      setDraft((cards) => cards.map((card, i) => (i === index ? { ...card, reference } : card))),
    setLayout: (index: number, layout: QuoteCardLayout) =>
      setDraft((cards) => cards.map((card, i) => (i === index ? { ...card, layout } : card))),
    reset: () => setDraft(set.cards),
    save: () => onSave(draft),
  }
}

/** A card's line under its words: "John 1:14 · BSB", "— Pastor Sam", or nothing. */
export function cardAttribution(card: QuoteCard): string {
  // A verse shown by its reference alone has it as its words: no caption under them.
  if (card.kind === 'verse')
    return card.text ? [card.reference, card.translation].filter(Boolean).join(' · ') : ''
  return card.reference ? `— ${card.reference}` : ''
}

/** The words a card shows: a verse with no wording shows its reference large instead. */
export function cardWords(card: QuoteCard): string {
  if (card.kind === 'verse' && !card.text) return card.reference
  const words = typographic(card.text.trim())
  return card.kind === 'quote' && words ? `“${words.replace(/^“|”$/g, '')}”` : words
}

/** A card export's file name: "Salt and Light – Quote 2 – Story.png". */
export function cardFileName(
  title: string,
  index: number,
  size: (typeof QUOTE_EXPORT_SIZES)[number],
) {
  return `${downloadName(title, `Card ${index + 1}`, size.label)}.png`
}
